import { Content, Conversation } from '@shared/types';
import {
  OutputFormat,
  getCachedOutputFormat,
  setCachedOutputFormat,
} from '@/lib/outputFormatCache';

/**
 * Coerce an unknown thrown value into a human-readable string.
 *
 * Supabase/PostgREST rejects a query with a plain `PostgrestError` object
 * ({ message, code, details, hint }) — NOT an `Error` instance. The naive
 * `e instanceof Error ? e.message : String(e)` renders those as the useless
 * "[object Object]", which is exactly what a failed SUPIN artifact insert
 * (e.g. an oversized K2 STL payload) surfaced in the chat bubble. Pull the
 * real message + code out so failures are actually diagnosable.
 */
export function errToMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object') {
    const e = err as {
      message?: unknown;
      code?: unknown;
      details?: unknown;
    };
    if (typeof e.message === 'string' && e.message) {
      const code = e.code != null ? ` [${String(e.code)}]` : '';
      const details =
        typeof e.details === 'string' && e.details ? ` — ${e.details}` : '';
      return `${e.message}${code}${details}`;
    }
    try {
      return JSON.stringify(err);
    } catch {
      /* circular / non-serialisable — fall through */
    }
  }
  return String(err);
}

/**
 * The postmortem that already solved the two most common compute failures on
 * this path. Every enriched message points here so whoever hits the failure
 * lands on the established root cause + fix instead of re-deriving it.
 */
export const COMPUTE_ERROR_DOC =
  'docs/postmortems/2026-06-03-executor-lock-and-unitcell-timeout.md';

/**
 * Make a known compute-failure string legible.
 *
 * Two failures on the generation path historically surfaced as bare, cause-less
 * status codes that forced the reader to re-derive an already-solved root cause
 * (see {@link COMPUTE_ERROR_DOC}, Prevention #6 "Error legibility"):
 *
 *  - **unitcell / microservice `504 IDLE_TIMEOUT`** — the Fly sidecar was
 *    cold-starting or the solve ran past Supabase's ~150s edge idle window with
 *    no keepAlive bytes, so the runtime idle-killed the request. NOT a bad
 *    request.
 *  - **SUPIN `401`/`403`** — SUPIN is the lone browser-direct kernel, so it
 *    ships the user's Supabase JWT and the runner validates it against its OWN
 *    project; a staging session against the prod-configured runner fails auth.
 *
 * Pure + idempotent: appends a cause + doc-pointer for a recognised failure and
 * returns any other message UNCHANGED (never swallows an unknown error). Safe to
 * apply more than once — a message already carrying the doc link is returned
 * as-is.
 */
export function explainComputeError(message: string): string {
  if (!message) return message;
  // Already enriched (idempotent) — don't append the cause twice.
  if (message.includes(COMPUTE_ERROR_DOC)) return message;

  const isIdleTimeout =
    /IDLE_TIMEOUT/i.test(message) ||
    (/\b504\b/.test(message) && /unitcell|microservice/i.test(message));
  if (isIdleTimeout) {
    return (
      `${message}\n\nProbable cause: the unitcell/microservice runner was ` +
      `cold-starting or the solve ran past Supabase's ~150s edge idle window ` +
      `(no keepAlive bytes flowed → the runtime idle-killed it), not a bad ` +
      `request. See ${COMPUTE_ERROR_DOC}.`
    );
  }

  const isSupinAuth = /SUPIN/i.test(message) && /\b(401|403)\b/.test(message);
  if (isSupinAuth) {
    return (
      `${message}\n\nProbable cause: SUPIN is the lone browser-direct kernel, ` +
      `so it ships your Supabase JWT and the runner validates it against its ` +
      `own project — a staging session hitting the prod-configured runner ` +
      `fails auth (an environment mismatch, not a bad token). ` +
      `See ${COMPUTE_ERROR_DOC}.`
    );
  }

  return message;
}

// Decide which CAD kernel a parametric request should route to.
// Priority chain (most-trusted → least-trusted):
//   1. conversation.settings.outputFormat  — DB-canonical, set at creation
//   2. localStorage cache                  — cold-start fallback if settings is missing
//   3. content.outputFormat                — first-message preference from the dropdown
//   4. 'mesh'                              — final default
//
// Side effects (self-healing):
//   - If settings has a value, refresh the localStorage cache (keeps cold-start
//     fast and recovers from any drift caused by the user editing dev tools or
//     clearing storage and reloading mid-session).
//   - The settings → localStorage write happens here; the reverse direction
//     (writing localStorage's value back into settings when settings is empty)
//     is handled in ChatSection, which has the Supabase client and the
//     conversation row already.
export function resolveOutputFormat(
  conversation: Pick<Conversation, 'id' | 'settings'>,
  content?: Pick<Content, 'outputFormat'>,
): OutputFormat {
  const fromSettings = conversation.settings?.outputFormat;
  if (
    fromSettings === 'mesh' ||
    fromSettings === 'solidbody' ||
    fromSettings === 'implicit'
  ) {
    // Settings is canonical — keep the cache in sync so a later cold start
    // doesn't briefly disagree with the DB.
    setCachedOutputFormat(conversation.id, fromSettings);
    return fromSettings;
  }
  const fromCache = getCachedOutputFormat(conversation.id);
  if (fromCache) return fromCache;
  const fromContent = content?.outputFormat;
  if (
    fromContent === 'mesh' ||
    fromContent === 'solidbody' ||
    fromContent === 'implicit'
  ) {
    return fromContent;
  }
  return 'mesh';
}
